/**
 * RideUpdateListener
 *
 * Global rider-side listener. Polls `/api/rideshare/bookings` every 8 s
 * (plus instant re-polls on socket / push events) and shows every live
 * driver counter offer in a persistent slide-up with its reply timer.
 * The sheet only closes when the customer accepts, counters back or
 * declines — or the offer's window runs out.
 */
import { playSoftChime } from "@/lib/chime";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Car, Check, MessageSquare, Loader2, X } from "lucide-react";
import { API, useAuth } from "@/App";
import { titleTerm } from "@/utils/rideshareTerms";
import CircularCountdown from "@/components/CircularCountdown";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { offerWindow, windowLive, notifyBookingUpdated } from "@/lib/requestWindow";

const POLL_MS = 8000;
const OPEN_STATUSES = new Set(["negotiating", "pending"]);
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace("https://", "wss://").replace("http://", "ws://");

// Every open driver offer on a trip — each one is its own decision for the rider.
const openDriverOffers = (trip) => (Array.isArray(trip?.offers) ? trip.offers : [])
  .filter((o) => o?.status === "open" && o?.by === "driver");

const offerSig = (trip, o) => `${trip.id}|${o.id}|${o.amount}|${o.counter_count || 0}|${o.created_at || ""}`;
const win = (trip, offer) => offerWindow(offer, trip.updated_at || trip.created_at);

export default function RideUpdateListener({ demo }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState(() => (demo ? [{ trip: demo.trip, offer: demo.offer, sig: "demo" }] : [])); // [{ trip, offer, sig }]
  const [gone, setGone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [counterOpen, setCounterOpen] = useState(!!demo?.counterOpen);
  const [counterAmount, setCounterAmount] = useState(demo?.counterAmount || "");
  const seenRef = useRef(new Set());

  const current = queue[0] || null;
  const trip = current?.trip;
  const offer = current?.offer;
  const currentSig = current?.sig;

  const pollTrips = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/rideshare/bookings?token=${token}`);
      const trips = (res.data || []).filter((t) => OPEN_STATUSES.has(t.status) && t.customer_id === user?.id);
      const live = trips.flatMap((t) => openDriverOffers(t).map((o) => ({ trip: t, offer: o, sig: offerSig(t, o) })))
        .filter((it) => windowLive(win(it.trip, it.offer)));
      setQueue((prev) => {
        const key = (it) => `${it.trip.id}|${it.offer.id}`;
        const byKey = new Map(live.map((it) => [key(it), it]));
        const kept = prev.filter((it, i) => i === 0 || byKey.has(key(it))).map((it) => byKey.get(key(it)) || it);
        const keys = new Set(kept.map(key));
        const fresh = live.filter((it) => !keys.has(key(it)) && !seenRef.current.has(it.sig));
        [...kept, ...fresh].forEach((it) => seenRef.current.add(it.sig));
        if (fresh.length) playSoftChime(fresh[0].sig);
        return fresh.length || kept.some((it, i) => it !== prev[i]) ? [...kept, ...fresh] : prev;
      });
    } catch (_) { /* silent — transient network blip */ }
  }, [token, user?.id]);

  // Poll while signed in as a customer.
  useEffect(() => {
    if (demo) return undefined;
    seenRef.current = new Set();
    setQueue([]);
    setCounterOpen(false);
    setCounterAmount("");
    if (!token || user?.role !== "customer") return undefined;
    pollTrips();
    const id = setInterval(pollTrips, POLL_MS);
    return () => clearInterval(id);
  }, [token, user?.role, pollTrips, demo]);

  // Instant re-poll on socket / push events about offers.
  useEffect(() => {
    if (!token || user?.role !== "customer" || !user?.id) return undefined;
    const onEvt = (ev) => {
      const t = String(ev?.detail?.type || "");
      if (t.startsWith("rideshare_") || t.startsWith("offer_")) pollTrips();
    };
    window.addEventListener("yorm:booking-updated", onEvt);
    let ws = null; let retry = null; let closed = false;
    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws/${user.id}?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const t = String(JSON.parse(e.data)?.type || "");
            if (t.startsWith("rideshare_") || t.startsWith("offer_")) setTimeout(pollTrips, 300);
          } catch (_) { /* non-JSON ping */ }
        };
        ws.onclose = () => { if (!closed) retry = setTimeout(connect, 5000); };
      } catch (_) { retry = setTimeout(connect, 5000); }
    };
    connect();
    return () => {
      closed = true; clearTimeout(retry);
      try { ws?.close(); } catch (_) { /* already closed */ }
      window.removeEventListener("yorm:booking-updated", onEvt);
    };
  }, [token, user?.role, user?.id, pollTrips]);

  // Watch the offer on screen: withdrawn / taken / cancelled → explain and move on.
  useEffect(() => {
    if (!trip?.id || !offer?.id || !token || demo) return undefined;
    setGone(null);
    let stop = false;
    const check = async () => {
      try {
        const { data: b } = await axios.get(`${API}/rideshare/bookings/${trip.id}?token=${token}`);
        if (stop || !b) return;
        const o = (b.offers || []).find((x) => x?.id === offer.id);
        if (o && o.status === "accepted") { dismiss(); return; }
        const reason = b.status === "cancelled" ? "This trip was cancelled."
          : ["accepted", "in_progress", "completed"].includes(b.status) ? "This trip already has a driver."
          : o && o.status === "expired" ? "Time's up — this offer has lapsed."
          : o && o.status !== "open" ? "This offer is no longer open." : null;
        if (reason) setGone(reason);
      } catch (e) {
        if (!stop && e?.response?.status === 404) setGone("This trip is no longer available.");
      }
    };
    const iv = setInterval(check, 3000);
    return () => { stop = true; clearInterval(iv); };
  }, [trip?.id, offer?.id, currentSig, token, demo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!gone) return undefined;
    const t = setTimeout(() => dismiss(), 4000);
    return () => clearTimeout(t);
  }, [gone]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    setGone(null);
    setCounterOpen(false);
    setCounterAmount("");
    setQueue((prev) => prev.slice(1).filter((it) => windowLive(win(it.trip, it.offer))));
  };

  const respond = async (action) => {
    if (!trip?.id || !offer?.id) return;
    setBusy(true);
    try {
      await axios.post(`${API}/rideshare/bookings/${trip.id}/offer?token=${token}`, { action, target_offer_id: offer.id });
      notifyBookingUpdated(`offer_${action}ed`, trip.id);
      const id = trip.id;
      dismiss();
      if (action === "accept") { toast.success("Driver offer accepted — tracking trip"); navigate(`/customer/trip/${id}`); }
      else toast.success("Offer declined");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action === "accept" ? "Accept" : "Decline"} failed`);
    } finally {
      setBusy(false);
    }
  };

  const sendCounterBack = async () => {
    if (!trip?.id || !offer?.driver_id) return;
    const amt = parseFloat(counterAmount);
    if (!amt || amt <= 0) { toast.error("Enter a positive GHS amount"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/rideshare/bookings/${trip.id}/offer?token=${token}`, { action: "counter", amount: amt, target_driver_id: offer.driver_id });
      toast.success(`Counter offer of GHS ${amt.toFixed(2)} sent`);
      notifyBookingUpdated("offer_countered", trip.id);
      dismiss();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Counter failed");
    } finally {
      setBusy(false);
    }
  };

  if (!trip || !offer) return null;

  const partyTerm = titleTerm(trip.rideshare_type);
  const fareStr = offer.amount != null ? Number(offer.amount).toFixed(2) : null;
  const km = trip.estimated_distance_km != null ? Number(trip.estimated_distance_km).toFixed(1) : null;
  const previous = offer.previous_amount != null ? Number(offer.previous_amount).toFixed(2) : null;
  const driverName = offer.driver_name || trip.artisan_name || `Your ${partyTerm.toLowerCase()}`;
  const w = win(trip, offer);

  return (
    <PersistentSheet open title={`${partyTerm} counter offer`} icon={Car} testId="rider-update-modal">
        {gone && (
          <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-[15px] font-semibold text-red-700 text-center" data-testid="rider-update-gone">
            {gone}
          </div>
        )}
        <div className="space-y-3">
          <div className="rounded-2xl text-white p-4 bg-gradient-to-br from-[#4F988E] to-[#3d7a72] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-white">{driverName}'s price</p>
              <p className="text-3xl font-bold text-white" data-testid="rider-update-fare">
                {fareStr ? `GHS ${fareStr}` : "—"}
              </p>
              {previous && previous !== fareStr && (
                <p className="text-xs text-white mt-1" data-testid="rider-update-previous">
                  <span className="line-through opacity-75">GHS {previous}</span>
                  <span className="ml-2">→ new price</span>
                </p>
              )}
              <p className="text-xs text-white mt-1">
                {km ? `${km} km` : ""}
                {trip.rideshare_type ? ` · ${trip.rideshare_type}` : ""}
              </p>
            </div>
            <div className="shrink-0 bg-white/95 rounded-xl p-2 shadow-inner">
              <CircularCountdown
                startedAt={w.startedAt}
                totalSec={w.totalSec}
                size={72}
                stroke={6}
                testId="rider-update-countdown"
                onExpire={() => setGone("Time's up — this offer has lapsed.")}
              />
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-green-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Pickup</p>
                <p className="text-gray-800">{trip.pickup_address || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Drop-off</p>
                <p className="text-gray-800">{trip.dropoff_address || "—"}</p>
              </div>
            </div>
          </div>

          {gone ? (
            <Button variant="outline" onClick={dismiss} className="w-full rounded-full" data-testid="rider-update-close">Close</Button>
          ) : counterOpen ? (
            <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="rider-counter-form">
              <label className="text-xs font-semibold text-gray-700">Your counter offer (GHS)</label>
              <div className="flex gap-2">
                <Input type="number" min="1" step="0.5" value={counterAmount} onChange={(e) => setCounterAmount(e.target.value)}
                  placeholder={fareStr || "0.00"} className="flex-1" data-testid="rider-counter-input" autoFocus />
                <Button onClick={sendCounterBack} disabled={busy} className="bg-[#4F988E] hover:bg-[#3d7a72] text-white" data-testid="rider-counter-send">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : "Send"}
                </Button>
              </div>
              <button type="button" onClick={() => { setCounterOpen(false); setCounterAmount(""); }}
                className="text-xs text-gray-500 hover:text-gray-700" data-testid="rider-counter-back">
                Back
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCounterOpen(true); setCounterAmount(fareStr || ""); }} disabled={busy}
                className="rounded-full" data-testid="rider-counter-back-btn">
                <MessageSquare size={16} className="mr-1" />
                Counter back
              </Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full" onClick={() => respond("accept")} disabled={busy} data-testid="rider-accept-driver">
                {busy ? <Loader2 size={16} className="animate-spin" /> : (<><Check size={16} className="mr-1" />Accept GHS {fareStr}</>)}
              </Button>
              <Button variant="outline" onClick={() => respond("decline")} disabled={busy}
                className="col-span-2 rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="rider-decline-driver">
                <X size={16} className="mr-1" />
                Decline
              </Button>
              {queue.length > 1 && (
                <p className="col-span-2 text-center text-xs text-gray-500" data-testid="rider-update-queue-count">
                  {queue.length - 1} more offer{queue.length > 2 ? "s" : ""} waiting
                </p>
              )}
            </div>
          )}
        </div>
    </PersistentSheet>
  );
}
